<template>
  <vz-skeleton
    v-if="isLoadBlock"
    class="mb-10"
    height="64"
  />
  <div
    v-else
    class="vz-pup mb-10"
    :class="{'vz-pup-inactive': !isActive, 'mb-30': info }"
  >
    <div class="vz-pup-icon">
      <slot name="icon"/>
    </div>

    <div class="vz-pup-body">
      <div class="vz-pup-body-name">
        {{ textButton }}
      </div>
      <div class="vz-pup-body-price">
        <vz-select
          v-if="hasAnotherCurrency"
          class="vz-pup-body-price-flag mb-0 mr-5 tiny-arrow"
          :modelValue="orderCurrency"
          :items="phones"
          list-class="vz-order-total-price-cost-country-list"
          @change="onChangeCurrency"
          @mousedown.native.stop
          @mouseup.native.stop
          @touchstart.native.stop
          @touchend.native.stop
        />
        <img
          v-else
          :src="currencyFlag"
          width="16"
          height="16"
        >
        <div
          class="vz-pup-body-price-total"
          :class="{'vz-pup-body-price-total-active': isLoadPrice}"
        >
          <span v-if="!isLoadPrice">{{ totalPrice }}</span>
        </div>
      </div>
    </div>

    <div
      v-if="info && !isLoadPrice"
      class="vz-pup-info"
    >
      {{ info }}
    </div>
  </div>
</template>

<script setup lang="ts">
import {useOrderStore} from "~/stores/order";
import VzSkeleton from "~/components/ui-kit/vz-skeleton.vue";
import {PhoneItem, SelectItem} from "~/types/types";
import {phoneCountries} from "~/common/objects";
import VzSelect from "~/components/ui-kit/vz-select.vue";

const props = defineProps({
  textButton: {
    type: String,
    default: ''
  },
  totalPrice: {
    type: String,
    default: ''
  },
  info: {
    type: String,
    default: ''
  },
  isActive: {
    type: Boolean,
    default: false
  },
  isLoadPrice: {
    type: Boolean,
    default: false
  },
  isLoadBlock: {
    type: Boolean,
    default: false
  }
})
const orderStore = useOrderStore()

const currencyFlag = computed(() => {
  return orderStore.orderPriceCurrency.flag
})

const currencyMeasure = computed(() => {
  return orderStore.orderPriceCurrency.currency
})

const hasAnotherCurrency = computed(() => {
  return phones.value.length > 1 && !useRoute().params.guid
})

const orderCurrency = computed(() => {
  return orderStore.orderForm.currency || 'RU'
})

const phones = computed((): PhoneItem[] => {
  const items: PhoneItem[] = [phoneCountries.find(p => p.value === 'RU') as PhoneItem]

  if (orderStore.orderForm.totalPrice?.currency?.KZ !== undefined) {
    items.push(phoneCountries.find(p => p.value === 'KZ') as PhoneItem)
  }

  if (orderStore.orderForm.totalPrice?.currency?.BY !== undefined) {
    items.push(phoneCountries.find(p => p.value === 'BY') as PhoneItem)
  }

  if (orderStore.orderForm.totalPrice?.currency?.AM !== undefined) {
    items.push(phoneCountries.find(p => p.value === 'AM') as PhoneItem)
  }

  if (orderStore.orderForm.totalPrice?.currency?.KG !== undefined) {
    items.push(phoneCountries.find(p => p.value === 'KG') as PhoneItem)
  }
  return items
})

async function onChangeCurrency({value}: SelectItem): Promise<void> {
  orderStore.orderForm.currency = value.toString()
  orderStore.getOrderPriceAction()
}
</script>

<style lang="scss" scoped>
.vz-pup {
  position: relative;
  border: 1px solid $primary;
  padding: 20px 20px 20px 50px;
  border-radius: 4px;
  cursor: pointer;

  &-inactive {
    border: 1px solid $border-gray;

    .vz-pup-body, .vz-pup-icon {
      filter: grayscale(100%);
      opacity: 80%;
      transition: all 130ms ease-in-out;
    }

    &:hover {
      .vz-pup-body, .vz-pup-icon {
        filter: grayscale(0%);
        opacity: 100%;
      }
    }
  }

  &-icon {
    position: absolute;
    left: 20px;
    top: 21px;
  }

  &-body {
    display: flex;
    flex-wrap: wrap;
    gap: 10px 17px;
    align-items: center;
    justify-content: space-between;

    &-name {
      width: 122px;
      font-size: 16px;
      font-weight: 500;
      line-height: 22px;
    }

    &-price {
      display: flex;
      gap: 5px;
      line-height: 22px;

      &-flag {
        margin-left: -17px;
      }

      &-total {
        font-weight: 500;
        font-size: 20px;
        color: $font-dark;
        line-height: 22px;

        &-active {
          &:after {
            font: normal normal normal 24px/1 "Material Design Icons";
            display: block;
            color: $font-light;
            right: 10px;
            font-size: 18px;
            top: calc(25% + 2px);
            content: "\F0E7B" !important;
            pointer-events: none;
            transform: rotate(0deg);
            transition: transform 0.15s ease;
            animation: rotating .35s linear infinite;
          }
        }
      }
    }
  }

  &-info {
    position: absolute;
    padding: 4px 12px;
    bottom: -11px;
    right: 20px;
    background: $primary;
    border-radius: 20px;
    font-size: 12px;
    color: $white;
    font-weight: 600;
    line-height: 14px;
  }
}
</style>
